import React, { useEffect, useRef } from "react";
import PartnerEntry from "./PartnerEntry";
import {
    MAX_PARTNERS_PER_LIST,
    OPERATION_TYPES,
    OPERATION_TYPE_PROFILES,
    SUPPLY_CHAIN_COPY,
    emptyPartnerRow,
    getListRules,
    visibleDirections,
} from "../../constants/supplyChain";
import "./SupplyChainPartners.css";

/**
 * The supply-chain editor: Vendors (upstream) and Customers (downstream), shared by
 * Registration, Settings and the Admin create forms. Fully controlled.
 *
 * Props:
 *   idPrefix           unique DOM id prefix
 *   profile            the license's business type (drives which lists show + minimums)
 *   rows / onChange    editor rows (see constants/supplyChain.js)
 *   errors             from validatePartners(): row fields, per-list, operationType
 *   searchMode         "public" (registration) | "auth"
 *   licenseNo          the reporting license — excluded from search results
 *   operationType / onOperationTypeChange   shown for growers when the handler is given
 *   disabled
 *
 * Accessibility: each list is a labelled group with its help text and list error attached;
 * focus moves to the new partner on add and to a neighbour (or the Add button) on remove.
 */
export default function SupplyChainPartnersEditor({
    idPrefix = "supply-chain",
    profile,
    rows,
    onChange,
    errors = {},
    searchMode = "auth",
    licenseNo = null,
    operationType,
    onOperationTypeChange,
    disabled = false,
}) {
    const entryRefs = useRef({});
    const addRefs = useRef({});
    const pendingFocus = useRef(null); // { key } | { add: direction }
    const rules = getListRules(profile);

    useEffect(() => {
        const target = pendingFocus.current;
        if (!target) return;
        pendingFocus.current = null;
        if (target.add) addRefs.current[target.add]?.focus();
        else entryRefs.current[target.key]?.focus();
    }, [rows.length]);

    const add = (direction) => {
        const row = emptyPartnerRow(direction);
        pendingFocus.current = { key: row.key };
        onChange([...rows, row]);
    };
    const remove = (row) => {
        const siblings = rows.filter((r) => r.direction === row.direction);
        const i = siblings.indexOf(row);
        const neighbour = siblings[i - 1] || siblings[i + 1];
        pendingFocus.current = neighbour ? { key: neighbour.key } : { add: row.direction };
        onChange(rows.filter((r) => r !== row));
    };
    const update = (next) => onChange(rows.map((r) => (r.key === next.key ? next : r)));

    const showOperationType = OPERATION_TYPE_PROFILES.includes(profile) && onOperationTypeChange;

    return (
        <div className="scp-editor">
            <p className="reg-section-hint">{SUPPLY_CHAIN_COPY.sectionHint}</p>

            {showOperationType && (
                <div className="scp-field scp-operation-type">
                    <label htmlFor={`${idPrefix}-operationType`} className="scp-label scp-required">
                        {SUPPLY_CHAIN_COPY.operationType.label}
                    </label>
                    <select
                        id={`${idPrefix}-operationType`}
                        className={`scp-input${errors.operationType ? " scp-input--error" : ""}`}
                        value={operationType || ""}
                        onChange={(e) => onOperationTypeChange(e.target.value)}
                        disabled={disabled}
                        aria-required="true"
                        aria-invalid={errors.operationType ? "true" : undefined}
                        aria-describedby={`${idPrefix}-operationType-help${errors.operationType ? ` ${idPrefix}-operationType-error` : ""}`}
                    >
                        <option value="">{SUPPLY_CHAIN_COPY.operationType.placeholder}</option>
                        {OPERATION_TYPES.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                    <p id={`${idPrefix}-operationType-help`} className="scp-hint">
                        {SUPPLY_CHAIN_COPY.operationType.help}
                    </p>
                    {errors.operationType && (
                        <span id={`${idPrefix}-operationType-error`} role="alert" className="scp-error">
                            {errors.operationType}
                        </span>
                    )}
                </div>
            )}

            {visibleDirections(profile).map((direction) => {
                const copy = SUPPLY_CHAIN_COPY.lists[direction];
                const list = rows.filter((r) => r.direction === direction);
                const required = rules[direction] > 0;
                const labelId = `${idPrefix}-${direction}-label`;
                const helpId = `${idPrefix}-${direction}-help`;
                const errorId = `${idPrefix}-${direction}-error`;
                return (
                    <section
                        key={direction}
                        className="scp-list"
                        role="group"
                        aria-labelledby={labelId}
                        aria-describedby={[helpId, errors[direction] ? errorId : null].filter(Boolean).join(" ")}
                    >
                        <div className="scp-list-header">
                            <h4 id={labelId} className={`scp-list-title${required ? " scp-required" : ""}`}>
                                {copy.label}
                            </h4>
                            <span className={`scp-badge ${required ? "scp-badge--required" : "scp-badge--optional"}`}>
                                {required ? SUPPLY_CHAIN_COPY.requirement.required : SUPPLY_CHAIN_COPY.requirement.optional}
                            </span>
                        </div>
                        <p id={helpId} className="scp-hint">
                            {copy.help}
                        </p>

                        {list.map((row, i) => (
                            <PartnerEntry
                                key={row.key}
                                ref={(el) => {
                                    entryRefs.current[row.key] = el;
                                }}
                                row={row}
                                index={i}
                                itemLabel={copy.itemLabel}
                                onChange={update}
                                onRemove={() => remove(row)}
                                errors={errors}
                                searchMode={searchMode}
                                excludeLicenseNo={licenseNo}
                                disabled={disabled}
                            />
                        ))}

                        {errors[direction] && (
                            <span id={errorId} role="alert" className="scp-error">
                                {errors[direction]}
                            </span>
                        )}

                        <div className="scp-list-footer">
                            <button
                                type="button"
                                ref={(el) => {
                                    addRefs.current[direction] = el;
                                }}
                                className="scp-add"
                                onClick={() => add(direction)}
                                disabled={disabled || list.length >= MAX_PARTNERS_PER_LIST}
                            >
                                {copy.addLabel}
                            </button>
                            <span className="scp-hint" aria-live="polite">
                                {list.length >= MAX_PARTNERS_PER_LIST
                                    ? `Maximum of ${MAX_PARTNERS_PER_LIST} reached`
                                    : `${list.length} of ${MAX_PARTNERS_PER_LIST}`}
                            </span>
                        </div>
                    </section>
                );
            })}
        </div>
    );
}
